import classNames from "classnames";
import { motion, useReducedMotion, type Transition } from "framer-motion";
import React from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import type { BackendNotificationPayload } from "../../../../shared/utility/mcp-backend-notifications";
import KuikaRevealTooltip from "../../../ui-kit/kuika-reveal-tooltip/kuika-reveal-tooltip";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";

interface IToolbarMenuItemProps {
  /** Icon asset name under `public/images/topbar-icons` — also the `data-testid` seed. */
  name: string;
  /** Human-readable dashboard name. Rendered only while the item is active. */
  label: string;
  condition: boolean;
  active?: boolean;
  onClick: () => void;
  onboardingId?: string;
  shimmer?: boolean;
  shimmerDelete?: boolean;
  notifications?: BackendNotificationPayload[];
  [key: string]: any;
}

const EMPTY_NOTIFICATIONS: BackendNotificationPayload[] = [];

/**
 * Shared-layout id of the one active-tab fill. Exactly one item renders the pill at a time, so
 * framer-motion projects it from the old tab's box to the new one instead of cross-fading two
 * fills — the same mechanism the board's view-tab pill uses.
 */
const ACTIVE_PILL_LAYOUT_ID = "kuika-toolbar-active-pill";

/**
 * Weighty spring: the fill takes its time crossing the row and settles with a trace of overshoot
 * rather than snapping. Deliberately slower than the board's snappier `board-view-active-pill`
 * (480/38) — a dashboard switch is a bigger move over a longer row, and at that stiffness the
 * travel was over before the eye could follow it.
 */
const ACTIVE_PILL_TRANSITION: Transition = { type: "spring", stiffness: 170, damping: 24, mass: 1.2 };

const REDUCED_PILL_TRANSITION: Transition = { duration: 0 };

const ToolbarMenuItem: React.FC<IToolbarMenuItemProps & IWithSDProps & IWithContextProp> = ({
  name,
  label,
  condition,
  active = false,
  onClick,
  onboardingId,
  shimmer = false,
  shimmerDelete = false,
  notifications = EMPTY_NOTIFICATIONS,
  ...rest
}) => {
  const itemRef = React.useRef<HTMLDivElement>(null);
  // Honour the OS setting: the fill still moves to the new tab, it just gets there instantly.
  const prefersReducedMotion: boolean | null = useReducedMotion();

  if (!condition) {
    return null;
  }
  return (
    <div
      ref={itemRef}
      {...rest}
      className={classNames("kuika-toolbar__menu-item", {
        "kuika-toolbar__menu-item--active": active,
        "kuika-toolbar__menu-item--shimmer": shimmer && !shimmerDelete,
        "kuika-toolbar__menu-item--shimmer-delete": shimmerDelete
      })}
      onClick={onClick}
      data-onboarding-id={onboardingId}
      role="button"
      // Inactive items are icon-only, so the label is the only accessible name they have.
      aria-label={label}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onClick();
        }
      }}
    >
      {/* Behind the glyph and label (see the z-index pair in toolbar-left.scss), so the fill can
          travel across tabs without ever tinting their foreground. */}
      {active && (
        <motion.span
          layoutId={ACTIVE_PILL_LAYOUT_ID}
          className="kuika-toolbar__menu-item-pill"
          transition={prefersReducedMotion ? REDUCED_PILL_TRANSITION : ACTIVE_PILL_TRANSITION}
        />
      )}
      <KuikaSvg name={name} type="topbar-icons" width={20} height={20} pointer />
      {/* The label exists in the DOM only while active, so an inactive item reserves no dead space. */}
      {active && label && <KuikaTextMedium className="kuika-toolbar__menu-item-label">{label}</KuikaTextMedium>}
      {notifications.length > 0 && (
        <KuikaRevealTooltip anchorRef={itemRef} notifications={notifications} placement="bottom" />
      )}
    </div>
  );
};

const toolbarMenuItem = withContext<IToolbarMenuItemProps>(withSD<IToolbarMenuItemProps>(ToolbarMenuItem));
export { toolbarMenuItem as ToolbarMenuItem };
